/**
 * AutoModQueue — sidebar listing AutoMod-held messages with Y/N keyboard shortcuts.
 *
 * Y → approve currently-focused item
 * N → deny currently-focused item
 * J/K → navigate
 *
 * Mod-only.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModActions, type AutoModItem } from '../../hooks/useModActions.js';
import type { ApiError } from '../../lib/api.js';

const POLL_INTERVAL_MS = 15_000;

export default function AutoModQueue() {
  const [items, setItems] = useState<AutoModItem[]>([]);
  const [focusIdx, setFocusIdx] = useState<number>(0);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { getAutoModQueue, approveAutoMod, denyAutoMod } = useModActions();

  const refresh = useCallback(async () => {
    try {
      const list = await getAutoModQueue();
      setItems(list);
      setErrorMsg(null);
    } catch (err) {
      const apiErr = err as ApiError;
      setErrorMsg(`Fehler: ${apiErr?.status ?? '?'}`);
    }
  }, [getAutoModQueue]);

  useEffect(() => {
    void refresh();
    const id = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const safeFocus = useMemo(
    () => Math.min(Math.max(0, focusIdx), Math.max(0, items.length - 1)),
    [focusIdx, items.length],
  );

  const act = useCallback(
    async (which: 'approve' | 'deny') => {
      if (busy || items.length === 0) return;
      const target = items[safeFocus];
      if (!target) return;
      setBusy(true);
      try {
        if (which === 'approve') await approveAutoMod(target.message_id);
        else await denyAutoMod(target.message_id);
        setItems((prev) => prev.filter((x) => x.id !== target.id));
      } catch (err) {
        const apiErr = err as ApiError;
        setErrorMsg(`${which}: ${apiErr?.status ?? '?'}`);
      } finally {
        setBusy(false);
      }
    },
    [approveAutoMod, denyAutoMod, busy, items, safeFocus],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (!containerRef.current) return;
      // Only consume keys when our queue is focused (parent scope) and not in inputs
      const tag = (document.activeElement?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        void act('approve');
      } else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        void act('deny');
      } else if (e.key === 'j' || e.key === 'ArrowDown') {
        setFocusIdx((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        setFocusIdx((i) => Math.max(0, i - 1));
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act, items.length]);

  return (
    <div ref={containerRef} className="border-l border-slate-800 bg-slate-900/30 w-72 flex flex-col">
      <div className="px-3 py-2 border-b border-slate-800 text-xs font-semibold flex items-center justify-between">
        <span>AutoMod-Queue</span>
        <span className="text-slate-500">
          Y = ok · N = ablehnen · J/K = navigieren
        </span>
      </div>
      {errorMsg && <div className="px-3 py-1 text-xs text-red-400">{errorMsg}</div>}
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center text-slate-500 text-xs mt-6 px-4">
            Keine ausstehenden Nachrichten.
          </div>
        ) : (
          items.map((item, idx) => (
            <div
              key={item.id}
              className={`px-3 py-2 border-b border-slate-800 text-xs cursor-pointer ${
                idx === safeFocus ? 'bg-purple-900/30' : 'hover:bg-slate-800/40'
              }`}
              onClick={() => setFocusIdx(idx)}
            >
              <div className="flex items-center gap-1">
                <span className="font-semibold">{item.target_display_name}</span>
                {item.category && (
                  <span className="text-yellow-400">[{item.category}]</span>
                )}
              </div>
              <div className="text-slate-300 break-words">{item.text}</div>
              <div className="mt-1 flex gap-1">
                <button
                  type="button"
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFocusIdx(idx);
                    void act('approve');
                  }}
                  className="px-2 py-0.5 rounded bg-green-900/40 hover:bg-green-800/50 disabled:opacity-50"
                >
                  Erlauben (Y)
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFocusIdx(idx);
                    void act('deny');
                  }}
                  className="px-2 py-0.5 rounded bg-red-900/40 hover:bg-red-800/50 disabled:opacity-50"
                >
                  Ablehnen (N)
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
